import Tooltip from '@material-ui/core/Tooltip';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import CommonImage from '@components/CommonImage';
import LoaderComponent from '@components/LoaderComponent';

import { getShortName, isAdmin } from '@utils/Util';

const CustomDropdown = ({
	title = 'Select', // defulat view of the select input
	onItemClick, // onCLick the item
	items = [], // proccessed list of items
	selectedItem, // selected item
	toolTipLength = 40, // default toolTip value is 40
	onItemSearch, // on get searched response from the list
	isAddNew, // if add new option show
	showAddNewButton, // if add new button show
	onAddNewButtonClick, // handler for add new button click
	isShowSearch = true, // if search option show
	isDisabled = false, // if disabled
	customDesign, // if custom design provided
	isAddNewOnInline = false,
	searchPlaceholder = 'Search',
	itemsLength = items.length,
	isLoading = false,
	addNewButtonName = '',
	onGenerateNumberButton,
	isEditable = false,
	onEdit,
	dropdownIconPath = `${process.env.PUBLIC_URL}/images/chevron_down.svg`,
	multiple = false,
	identifier,
	selectedImage = '',
	selectedImageClass = 'selected-image',
	position = 'left',
	alwayShowAddNewButton = false, // only used in fabric add (Techpack view)
	borderBottom = false,
	borderIndex = null,
	clearLabel = ''
}) => {
	const filterData = useSelector((store) => store.filterFieldStore);
	const [searchTerm, setSearchTerm] = useState(null);
	const [isOpen, setIsOpen] = useState(false);
	const [selectedItems, setSelectedItems] = useState(multiple ? [] : null);
	const resultsRef = useRef();
	const searchRef = useRef();
	const dropdownRef = useRef();

	useEffect(() => {
		if (filterData?.clearAll) {
			setSelectedItems([]);
		}
	}, [filterData]);
	useEffect(() => {
		if (filterData?.clearOne) {
			setSelectedItems(filterData[identifier]);
		}
	}, [filterData]);

	const onOutSideClick = (e) => {
		let selectedElement = e.target;
		if (dropdownRef.current && !dropdownRef.current.contains(selectedElement)) {
			setIsOpen(false);
			setSearchTerm('');
		}
	};

	let results = !searchTerm
		? items
		: items.filter((item) => {
				if (onItemSearch) {
					return item.value;
				} else {
					if (item?.value) {
						const itemValue = item.value.toString().toLowerCase();
						const searchTermLowerCase = searchTerm.toLowerCase();
						return itemValue.includes(searchTermLowerCase);
					}
				}
			});

	// useEffect(() => {
	// 	document.addEventListener('click', onOutSideClick, { capture: true });
	// 	document.body.removeEventListener('keydown', onKeyDown);
	// 	return () => {
	// 		document.removeEventListener('click', onOutSideClick, { capture: true });
	// 		document.body.removeEventListener('keydown', onKeyDown);
	// 	};
	// }, []);

	useEffect(() => {
		if (items) {
			document.addEventListener('click', onOutSideClick, { capture: true });
			document.body.addEventListener('keydown', onKeyDown);
		} else {
			document.body.removeEventListener('keydown', onKeyDown);
		}
		return () => {
			document.removeEventListener('click', onOutSideClick, { capture: true });
			document.body.removeEventListener('keydown', onKeyDown);
		};
	}, []);

	useEffect(() => {
		searchRef.current && searchRef.current.focus();
	}, [isOpen]);

	useEffect(() => {
		if (onItemSearch && searchTerm !== null) {
			// Wait 4ms before copying the value of tempValue into value;
			const timeout = setTimeout(() => {
				onItemSearch(searchTerm);
			}, 400);
			// If the hook is called again, cancel the previous timeout
			// This creates a debounce instead of a delay
			return () => clearTimeout(timeout);
		}
	}, [searchTerm]);

	const onSearchItem = (event) => {
		setSearchTerm(event.target.value);
	};

	function onKeyDown(event) {
		const isUp = event.key === 'ArrowUp';
		const isDown = event.key === 'ArrowDown';
		const isTab = event.key === 'Tab';

		if (isTab) {
			setIsOpen(false);
			setSearchTerm('');
			return;
		}

		const inputIsFocused = document.activeElement === searchRef.current;
		let resultsItems = Array.from((resultsRef.current && resultsRef.current.children) || []);
		const activeResultIndex = resultsItems.findIndex((child) => {
			return child.querySelector('span') === document.activeElement;
		});

		if (isUp) {
			if (inputIsFocused) {
				resultsItems[resultsItems.length - 1].querySelector('span').focus();
			} else if (resultsItems[activeResultIndex - 1]) {
				resultsItems[activeResultIndex - 1].querySelector('span').focus();
			} else {
				searchRef.current && searchRef.current.focus();
			}
		}

		if (isDown) {
			if (inputIsFocused) {
				resultsItems[0].querySelector('span').focus();
			} else if (resultsItems[activeResultIndex + 1]) {
				resultsItems[activeResultIndex + 1].querySelector('span').focus();
			} else {
				searchRef.current && searchRef.current.focus();
			}
		}
	}

	const onSelectItem = (idx, value, checked) => {
		let item = { index: idx, title: title, isAddNew: false, ...value };
		if (multiple && value.id !== null) {
			if (checked) {
				const newSelectedItems = [...selectedItems, item].filter(
					(selectedItem) => selectedItem.id !== null && selectedItem.id !== undefined
				);
				onItemClick && onItemClick(newSelectedItems, multiple);
				setSelectedItems(newSelectedItems);
			} else {
				const updatedSelectedItems = selectedItems.filter(
					(selectedItem) => selectedItem.id !== value.id
				);
				onItemClick && onItemClick(updatedSelectedItems, multiple);
				setSelectedItems(updatedSelectedItems);
			}
		} else {
			setSelectedItems([item]);
			onItemClick && onItemClick(item, multiple);
			setSearchTerm('');
			setIsOpen(false); // Close dropdown or similar UI
		}
	};

	const onSelectClick = () => {
		setIsOpen(false);
		// onItemClick({ value: searchTerm });
		onItemClick && onItemClick({ isAddNew: true, value: searchTerm }, multiple);
		setSearchTerm('');
	};

	const onOpenDropdown = (e) => {
		if (e.key === 'Enter') {
			setIsOpen(true);
		}
	};

	const renderAddNewOnInline = () => (
		<div
			className="add-btn cursor-pointer"
			onClick={() => {
				onSelectClick();
			}}
			onKeyPress={(e) => {
				if (e.key === 'Enter') {
					onSelectClick();
				}
			}}
		>
			<span className="button text regular-14 gray_dark_01" tabIndex={0}>
				<span className="weight-500">ADD:</span> {searchTerm}
			</span>
		</div>
	);

	const renderAddNew = () => {
		return (
			<div className="add-btn cursor-pointer" onClick={onSelectClick} tabIndex={0}>
				<button className="button text">
					<svg
						width={14}
						height={14}
						viewBox="0 0 14 14"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
					>
						<path
							d="M13 8H8V13C8 13.55 7.55 14 7 14C6.45 14 6 13.55 6 13V8H1C0.45 8 0 7.55 0 7C0 6.45 0.45 6 1 6H6V1C6 0.45 6.45 0 7 0C7.55 0 8 0.45 8 1V6H13C13.55 6 14 6.45 14 7C14 7.55 13.55 8 13 8Z"
							fill="white"
						/>
					</svg>
					<span className="ml-2">Add {searchTerm}</span>
				</button>
			</div>
		);
	};

	const renderAddNewButton = () => {
		return (
			<div
				className="add-btn cursor-pointer"
				onClick={() => {
					onAddNewButtonClick(searchTerm, setIsOpen);
				}}
				tabIndex={0}
			>
				<button className="button text">
					<svg
						width={14}
						height={14}
						viewBox="0 0 14 14"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
					>
						<path
							d="M13 8H8V13C8 13.55 7.55 14 7 14C6.45 14 6 13.55 6 13V8H1C0.45 8 0 7.55 0 7C0 6.45 0.45 6 1 6H6V1C6 0.45 6.45 0 7 0C7.55 0 8 0.45 8 1V6H13C13.55 6 14 6.45 14 7C14 7.55 13.55 8 13 8Z"
							fill="white"
						/>
					</svg>
					<span className="ml-2">Add {addNewButtonName ? addNewButtonName : searchTerm}</span>
				</button>
			</div>
		);
	};

	const renderGenerateButton = () => {
		return (
			<div
				className="add-btn cursor-pointer"
				onClick={() => {
					onGenerateNumberButton(searchTerm, setIsOpen);
				}}
				tabIndex={0}
			>
				<button className="button text">
					<img src={`${process.env.PUBLIC_URL}/icons/Update-gray.svg`} alt="generate" />
					<span className="ml-2">Generate New Number</span>
				</button>
			</div>
		);
	};

	const renderSelectedItem = (selected) => (
		<p className={`regular-14 ${selectedItem ? 'selected-item' : ''}`}>
			{selectedImage && (
				<CommonImage
					src={selectedImage}
					alt=""
					className={selectedImageClass}
					width={20}
					height={20}
				/>
			)}
			{selected}
		</p>
	);

	const renderSelectedItemBar = () => {
		return selectedItem ? (
			<Tooltip title={<span className="regular-14">{selectedItem}</span>} placement="top" arrow>
				{renderSelectedItem(getShortName(selectedItem, toolTipLength))}
			</Tooltip>
		) : (
			renderSelectedItem(title)
		);
	};

	return (
		<div className="select-component" ref={dropdownRef}>
			<>
				<div
					className={`click-box click mb-1 ${isDisabled ? 'disabled' : ''}`}
					onClick={() => {
						if (!isDisabled) {
							setIsOpen(!isOpen);
						}
					}}
					tabIndex={0}
					onKeyPress={onOpenDropdown}
				>
					{renderSelectedItemBar()}
					<img src={dropdownIconPath} alt="" />
				</div>
				{isOpen && !isLoading && (
					<LoaderComponent loading={isLoading}>
						<div className={`dropdown-box ${position === 'right' ? 'dropdown-menu-right' : ''}`}>
							{isShowSearch && (
								<div className="m-0 search-filter">
									<div className="with-icon right-icon">
										<input
											type="text"
											autoComplete="off"
											placeholder={searchPlaceholder}
											value={searchTerm || ''}
											onChange={onSearchItem}
											ref={searchRef}
										/>

										<span className="right-icon">
											<img src={`${process.env.PUBLIC_URL}/icons/search.png`} />
										</span>
									</div>
								</div>
							)}
							{onGenerateNumberButton && results.length === 0 && renderGenerateButton()}
							{isAddNew && results.length === 0 && renderAddNew()}
							{showAddNewButton && results.length === 0 && renderAddNewButton()}
							{alwayShowAddNewButton && renderAddNewButton()}
							<div className="color-list-box" ref={resultsRef}>
								{isAddNewOnInline && results.length === 0 && renderAddNewOnInline()}

								{results?.map(
									(item, idx) =>
										itemsLength > idx && (
											<React.Fragment key={`${item.id}_${idx}`}>
												<li
													className={`single-color-item single-item d-flex align-items-center  ${
														multiple
															? selectedItems?.some(
																	(selectedItem) =>
																		selectedItem.value === item.value
																)
																? 'selected'
																: ''
															: selectedItem === item.value
																? 'selected'
																: ''
													}${item?.isPrimary === true ? 'option-label' : ''} ${borderBottom && borderIndex === idx ? 'border-bottom' : ''}`}
													onClick={() => onSelectItem(idx, item, multiple)}
													onKeyPress={(e) => {
														if (e.key === 'Enter') {
															onSelectItem(idx, item, multiple);
														}
													}}
												>
													<span className="single-item-focus" tabIndex={0}>
														{customDesign ? (
															customDesign(item)
														) : (
															<>
																<span
																	style={
																		item?.value === clearLabel
																			? { color: '#F22B2B' }
																			: undefined
																	}
																>
																	{item.value}
																</span>
																{item?.secondLine && (
																	<p className="regular-12 gray_dark_03">
																		{item?.secondLine}
																	</p>
																)}
															</>
														)}
													</span>
													{(isAdmin() || isEditable) && onEdit && (
														<span
															className="mr-1 edit-pen"
															onClick={() => onEdit && onEdit(item)}
														>
															<CommonImage
																src="/icons/edit-black-20.svg"
																width={20}
															/>
														</span>
													)}
												</li>
											</React.Fragment>
										)
								)}
							</div>
						</div>
					</LoaderComponent>
				)}
			</>
		</div>
	);
};

export default CustomDropdown;
